import type { PropField, TrimStart } from './Core';
import type { ArrayValues } from 'type-fest';
import { DistinctFieldProp, FormField } from './Form';
import { FieldPath } from 'react-hook-form';

export type AddressFormConfigOrderedProps = [
  'salutation',
  'firstName',
  'lastName',
  'companyName',
  'addressLine1',
  'addressLine2',
  'country',
  'postcode',
  'city',
  'email',
  'phone',
];

export type AddressFormConfigProp = ArrayValues<AddressFormConfigOrderedProps>;

/**
 * A map of address props to values of type `T`.
 *
 * @example
 * type AddressFormFieldLabels = AddressPropMap<string>;
 * /// which is equals to:
 * type AddressFormFieldLabels = {
 *   salutation: string;
 *   firstName: string;
 *   ... 8 more ...;
 *   phone: string;
 * }
 */
export type AddressPropMap<T> = Record<AddressFormConfigProp, T>;

type AddressFieldName<Prop extends AddressFormConfigProp, Prefix extends string> = PropField<
  Prefix,
  TrimStart<Prop, 'address'>
>;

/**
 * Creates a type as map of address props to model fields.
 *
 * @example
 * type BillingAddressFields = AddressFields<'billingAddress'>
 * /// which is equals to:
 * type BillingAddressFields = {
 *   salutation: "billingAddressSalutation";
 *   firstName: "billingAddressFirstName";
 *   lastName: "billingAddressLastName";
 *   companyName: "billingAddressCompanyName";
 *   addressLine1: "billingAddressLine1";
 *   ... 5 more ...;
 *   phone: "billingAddressPhone";
 * }
 */
export type AddressFields<Prefix extends string> = {
  [K in AddressFormConfigProp]: AddressFieldName<K, Prefix>;
};

/** Map of address property to `FormField.fieldType` */
export const addressFormFieldTypes = {
  salutation: 'radio',
  firstName: 'text',
  lastName: 'text',
  companyName: 'text',
  addressLine1: 'text',
  addressLine2: 'text',
  country: 'select',
  postcode: 'text',
  city: 'text',
  email: 'text',
  phone: 'text',
} as const;

export type AddressFieldFieldTypes = typeof addressFormFieldTypes;

/**
 * Defines a data model that includes prefixed address fields.
 *
 * @example
 * type BillingAddress = AddressModel<'userBillingAddress'>;
 * /// which is equal to:
 *   type BillingAddress = {
 *       userBillingAddressSalutation: string;
 *       userBillingAddressFirstName: string;
 *       userBillingAddressLastName: string;
 *       userBillingAddressCompanyName: string;
 *       userBillingAddressLine1: string;
 *       ... 5 more ...;
 *       userBillingAddressPhone: string;
 *   }
 */
export type AddressModel<Prefix extends string> = {
  // For now, all address props are of type string. With a simple mapping, it can be extended with other value types.
  [P in AddressFormConfigProp as AddressFieldName<P, Prefix>]: string;
};

export type AnyAddressModel = Record<string, string>;

/**
 * Defines a map of MUI and react-form-specific control props for address fields.
 */
export type AddressFieldAuxProps<
  Model extends AnyAddressModel = AnyAddressModel,
  Fields extends AddressPropMap<FieldPath<Model>> = AddressPropMap<FieldPath<Model>>,
> = {
  [P in AddressFormConfigProp]: Partial<
    Omit<Extract<FormField<Model, Fields[P]>, { fieldType: AddressFieldFieldTypes[P] }>, DistinctFieldProp>
  >;
};

/**
 * Defines an address form config.
 */
export type AddressFormConfig<Model extends AnyAddressModel> = {
  [P in AddressFormConfigProp]: Extract<FormField<Model, FieldPath<Model>>, { fieldType: AddressFieldFieldTypes[P] }>;
};
